import {
  STORAGE_KEY_ENUM,
  TABLE_SCHEMA_PAGE_NAME_ENUM,
  PROJECT_STEP_STATUS_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM,
} from 'enums';
import {
  ProjectStepItem,
  ProjectStepGridRow,
  ProjectStepDetailRow,
  CreateProjectStepMode,
  ProjectStepCommentItem,
} from './types';
import {
  ChipsArray,
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import {
  CommentsCell,
  DetailDrawer,
  AddCommentPopover,
  DeleteCommentDialog,
  CreateProjectStepDialog,
  UpdateProjectStepDialog,
  DeleteProjectStepDialog,
} from './components';
import {
  Box,
  Chip,
  alpha,
  Button,
  Divider,
  Tooltip,
  useTheme,
  TextField,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
} from '@mui/material';
import {
  GridColDef,
  useGridApiRef,
  isAutogeneratedRow,
  GridActionsCellItem,
  GridRowHeightParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { ActivityType, Project, User } from 'library/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import CreateActivityBoardItemDialog, {
  InitialActivityData,
} from 'pages/activity-managing/components/create-activity-board-item-dialog';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { appendCommentToProjectStepTree, flattenProjectSteps, sortByDueDate } from './utils';
import { Authentication, renderAutoCompleteLocaleText, renderDataGridLocaleText, renderFormattedDate } from 'utils';

const ProjectStepManaging = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const pageName = TABLE_SCHEMA_PAGE_NAME_ENUM.PROJECT_STEP_MANAGING;

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [commentPopover, setCommentPopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    row: ProjectStepGridRow | null;
    loading: boolean;
  }>({
    open: false,
    anchorEl: null,
    row: null,
    loading: false,
  });
  const [statusType, setStatusType] = useState<number | null>(null);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [openActivityDialog, setOpenActivityDialog] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [projectSteps, setProjectSteps] = useState<ProjectStepItem[]>([]);
  const [selectedContact, setSelectedContact] = useState<User | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Project | null>(null);
  const [detailRow, setDetailRow] = useState<ProjectStepDetailRow | null>(null);
  const [openDeleteCommentDialog, setOpenDeleteCommentDialog] = useState(false);
  const [createMode, setCreateMode] = useState<CreateProjectStepMode>('main-task');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openCreateProjectStepDialog, setOpenCreateProjectStepDialog] = useState(false);
  const [openUpdateProjectStepDialog, setOpenUpdateProjectStepDialog] = useState(false);
  const [openDeleteProjectStepDialog, setOpenDeleteProjectStepDialog] = useState(false);
  const [rowIdForDeleteComment, setRowIdForDeleteComment] = useState<number | null>(null);
  const [commentIdWillBeDeleted, setCommentIdWillBeDeleted] = useState<number | null>(null);
  const [parentTaskForCreate, setParentTaskForCreate] = useState<ProjectStepGridRow | null>(null);
  const [activityInitialData, setActivityInitialData] = useState<InitialActivityData | null>(null);

  const gridRows = useMemo(() => flattenProjectSteps(projectSteps), [projectSteps]);

  const contactOptions = useMemo(
    () =>
      [...users].sort((a, b) =>
        (a.nameSurname ?? '').localeCompare(b.nameSurname ?? '', intl.locale, { sensitivity: 'base' })
      ),
    [users, intl.locale]
  );

  const selectedRow = useMemo(
    () => gridRows.find((row) => row.rowId === rowSelectionModel[0]) ?? null,
    [gridRows, rowSelectionModel]
  );

  const filteredActivityTypes = useMemo(() => {
    const me = users.find((user) => user.nameSurname === Authentication.getLoggedUser());

    if (!me?.roleId) return activityTypes;

    return activityTypes.filter((at) => at.roles.some((role) => role.id === me.roleId));
  }, [users, activityTypes]);

  const getTreeDataPath = useCallback((row: ProjectStepGridRow) => row.treePath, []);

  const openRowDetail = useCallback((row: ProjectStepGridRow) => {
    setDetailRow(row);
    setOpenDetail(true);
  }, []);

  const closeRowDetail = useCallback(() => {
    setOpenDetail(false);
    setDetailRow(null);
  }, []);

  const handleOpenAddComment = (row: ProjectStepGridRow, anchorEl: HTMLElement) => {
    setCommentPopover({
      open: true,
      anchorEl,
      row,
      loading: false,
    });
  };

  const handleCloseAddComment = () => {
    setCommentPopover((prev) => ({
      ...prev,
      open: false,
      anchorEl: null,
      row: null,
      loading: false,
    }));
  };

  const handleSubmitAddComment = async (commentText: string, row: ProjectStepGridRow) => {
    try {
      setCommentPopover((prev) => ({
        ...prev,
        loading: true,
      }));

      const response = await apiService.addCommentToProjectStep(row.id, commentText);

      if (response.isSuccess) {
        const newComment: ProjectStepCommentItem = {
          projectTaskId: row.id,
          userId: users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id ?? 0,
          commentText,
          createdAt: new Date().toISOString(),
          isDeleted: false,
          id: response.data.id,
        };

        setProjectSteps((prev) => appendCommentToProjectStepTree(prev, row.id, newComment));

        handleCloseAddComment();
      }
    } catch (error) {
      setCommentPopover((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleCloseDeleteCommentDialog = () => setOpenDeleteCommentDialog(false);
  const handleClickOpenDeleteCommentDialog = (comment: ProjectStepCommentItem, row: ProjectStepGridRow) => {
    setCommentIdWillBeDeleted(comment.id);
    setRowIdForDeleteComment(row.id);

    setOpenDeleteCommentDialog(true);
  };

  const handleCloseCreateProjectStepDialog = () => {
    setOpenCreateProjectStepDialog(false);
    setCreateMode('main-task');
    setParentTaskForCreate(null);
  };

  const handleClickOpenCreateProjectStepDialog = () => {
    setCreateMode('main-task');
    setParentTaskForCreate(null);
    setOpenCreateProjectStepDialog(true);
  };

  const handleClickOpenCreateSubTaskDialog = useCallback((row: ProjectStepGridRow) => {
    setCreateMode('sub-task');
    setParentTaskForCreate(row);
    setOpenCreateProjectStepDialog(true);
  }, []);

  const handleOpenActivityDialog = useCallback((row: ProjectStepGridRow) => {
    setActivityInitialData({
      bookId: row.bookId,
      projectId: row.projectId,
    });
    setOpenActivityDialog(true);
  }, []);

  const handleCloseActivityDialog = () => {
    setOpenActivityDialog(false);
    setActivityInitialData(null);
  };

  // translate functions
  const getProjectStepStatusTypeTranslated = useCallback(
    (projectStepStatusType: number) =>
      ({
        [PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED]: intl.formatMessage({
          id: 'enum.projectStepStatusType.requested',
          defaultMessage: 'İster',
        }),
        [PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS]: intl.formatMessage({
          id: 'enum.projectStepStatusType.inProgress',
          defaultMessage: 'Devam Ediyor',
        }),
        [PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED]: intl.formatMessage({
          id: 'enum.projectStepStatusType.completed',
          defaultMessage: 'Tamamlandı',
        }),
      })[projectStepStatusType] ?? '-',
    [intl]
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED,
        label: getProjectStepStatusTypeTranslated(PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED),
      },
      {
        value: PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS,
        label: getProjectStepStatusTypeTranslated(PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS),
      },
      {
        value: PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED,
        label: getProjectStepStatusTypeTranslated(PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED),
      },
    ],
    [getProjectStepStatusTypeTranslated]
  );

  const getProjectStepPriorityTypeTranslated = useCallback(
    (projectStepPriorityType: number) =>
      ({
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.nonUrgent',
          defaultMessage: 'Düşük',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.urgent',
          defaultMessage: 'Orta',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.veryUrgent',
          defaultMessage: 'Yüksek',
        }),
      })[projectStepPriorityType] ?? '-',
    [intl]
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT),
      },
    ],
    [getProjectStepPriorityTypeTranslated]
  );

  const getProjectStepResponsibleContactTypeTranslated = useCallback(
    (projectStepResponsibleContactType: number) =>
      ({
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customer',
          defaultMessage: 'Müşteri',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.idecon',
          defaultMessage: 'Idecon',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customerAndIdecon',
          defaultMessage: 'Müşteri / Idecon',
        }),
      })[projectStepResponsibleContactType] ?? '-',
    [intl]
  );

  const RESPONSIBLE_CONTACT_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(
          PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON
        ),
      },
    ],
    [getProjectStepResponsibleContactTypeTranslated]
  );

  const columns: GridColDef<ProjectStepGridRow>[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' }),
        width: 150,
        disableExport: true,
        getActions: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return [];
          }

          return [
            <Tooltip
              key="detail"
              title={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay Göster' })}
              arrow
              placement="top">
              <GridActionsCellItem
                icon={<VisibilityOutlinedIcon sx={{ color: 'primary.main', opacity: 0.85 }} />}
                label={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' })}
                onClick={() => openRowDetail(params.row)}
                showInMenu={false}
              />
            </Tooltip>,
            <PermissionHandler requiredPermissionCodes={['add-activity-board-item']}>
              <Tooltip
                title={intl.formatMessage({ id: 'effortTracing.enterActivity', defaultMessage: 'Aktivite Gir' })}
                arrow
                placement="top">
                <GridActionsCellItem
                  key="add-activity"
                  icon={<AddIcon sx={{ color: 'success.main', opacity: 0.85 }} />}
                  label={intl.formatMessage({ id: 'effortTracing.enterActivity', defaultMessage: 'Aktivite Gir' })}
                  onClick={() => handleOpenActivityDialog(params.row)}
                  showInMenu={false}
                />
              </Tooltip>
            </PermissionHandler>,
            ...(!params.row.isSubTask
              ? [
                  <PermissionHandler requiredPermissionCodes={['add-project-task']}>
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'projectStepManaging.createSubTask',
                        defaultMessage: 'Alt Adım Oluştur',
                      })}
                      arrow
                      placement="top">
                      <GridActionsCellItem
                        key="create-sub-task"
                        icon={<SubdirectoryArrowRightIcon sx={{ color: 'secondary.main', opacity: 0.9 }} />}
                        label={intl.formatMessage({
                          id: 'projectStepManaging.createSubTask',
                          defaultMessage: 'Alt Adım Oluştur',
                        })}
                        onClick={() => handleClickOpenCreateSubTaskDialog(params.row)}
                        showInMenu={false}
                      />
                    </Tooltip>
                  </PermissionHandler>,
                ]
              : []),
          ];
        },
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'projectStepManaging.book', defaultMessage: 'Proje' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => books.filter((book): book is Book => !!book && book.id != null).map((book) => book.id),
        getOptionLabel: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        valueFormatter: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        minWidth: 200,
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () =>
          products
            .filter((project): project is Project => !!project && project.id != null)
            .map((project) => project.id),
        getOptionLabel: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        valueFormatter: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        minWidth: 200,
      },
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projectStepManaging.projectStep', defaultMessage: 'Proje Adımı' }),
        minWidth: 200,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.stepExplanation',
          defaultMessage: 'Adım Açıklaması',
        }),
        minWidth: 200,
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'effortTracing.status', defaultMessage: 'Statü' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => STATUS_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return null;
          }

          const status = params.value ?? params.row?.status ?? PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED;
          const colorKey =
            status === PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED
              ? 'info'
              : status === PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS
                ? 'secondary'
                : 'success';

          return (
            <Chip
              size="small"
              label={getProjectStepStatusTypeTranslated(status)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
        minWidth: 200,
      },
      {
        field: 'priority',
        headerName: intl.formatMessage({ id: 'projectStepManaging.priority', defaultMessage: 'Öncelik' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => PRIORITY_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return null;
          }

          const priority = params.value ?? params.row?.priority ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT;
          const colorKey =
            priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
              ? 'success'
              : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
                ? 'warning'
                : 'error';

          return (
            <Chip
              size="small"
              label={getProjectStepPriorityTypeTranslated(priority)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
        minWidth: 200,
      },
      {
        field: 'relatedContactId',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.relatedContact',
          defaultMessage: 'İlgili Kişisi',
        }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => users.find((user) => user.id === value)?.nameSurname || '',
        minWidth: 200,
      },
      {
        field: 'alternativeRelatedContactId',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.alternativeRelatedContact',
          defaultMessage: 'Yedek İlgili Kişisi',
        }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => users.find((user) => user.id === value)?.nameSurname || '',
        minWidth: 200,
      },
      {
        field: 'responsibleContactType',
        headerName: intl.formatMessage({ id: 'productProjectPlan.responsibleContact', defaultMessage: 'Sorumlu' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => RESPONSIBLE_CONTACT_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => RESPONSIBLE_CONTACT_OPTIONS.find((x) => x.value === value)?.label ?? '',
        minWidth: 200,
      },
      {
        field: 'responsibleCustomerFullName',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.responsibleCustomerFullName',
          defaultMessage: 'Sorumlu Müşteri Ad Soyad',
        }),
        minWidth: 200,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({ id: 'effortTracing.createdAt', defaultMessage: 'Oluşturulma Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        minWidth: 150,
      },
      {
        field: 'updatedAt',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.lastUpdateDate',
          defaultMessage: 'Son Güncellenme',
        }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        minWidth: 150,
      },
      {
        field: 'estimatedTaskDay',
        headerName: intl.formatMessage({
          id: 'productProjectPlan.estimatedTaskDay',
          defaultMessage: 'Tahmini Adım Süresi (gün)',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 200,
        type: 'number',
      },
      {
        field: 'additionalEffortDay',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.additionalEffortDay',
          defaultMessage: 'Ek Efor Süresi (gün)',
        }),
        headerAlign: 'center',
        align: 'center',
        valueFormatter: (value) => value || '-',
        minWidth: 200,
        type: 'number',
      },
      {
        field: 'dueDate',
        headerName: intl.formatMessage({ id: 'projectStepManaging.terminateDate', defaultMessage: 'Termin' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        minWidth: 150,
      },
      {
        field: 'notes',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.notes',
          defaultMessage: 'Notlar',
        }),
        minWidth: 200,
      },
      {
        field: 'comments',
        headerName: intl.formatMessage({ id: 'projectStepManaging.comments', defaultMessage: 'Yorumlar' }),
        minWidth: 500,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        cellClassName: 'comments-cell',
        valueFormatter: (value: ProjectStepCommentItem[]) =>
          Array.isArray(value)
            ? value
                .map((comment) => {
                  if (!comment) return '';
                  const userName =
                    users.find((user) => user.id === comment.userId)?.nameSurname ?? 'Bilinmeyen Kullanıcı';
                  const text = comment.commentText ?? '';
                  return `${userName}: ${text}`;
                })
                .filter(Boolean)
                .join('\r\n')
            : '',
        renderCell: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return null;
          }
          return (
            <CommentsCell<ProjectStepGridRow>
              row={params.row}
              comments={params.value ?? []}
              currentUserId={users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id ?? 0}
              onAddComment={handleOpenAddComment}
              onDeleteComment={handleClickOpenDeleteCommentDialog}
              users={users}
            />
          );
        },
      },
    ],
    [
      intl,
      users,
      books,
      theme,
      products,
      openRowDetail,
      STATUS_OPTIONS,
      PRIORITY_OPTIONS,
      RESPONSIBLE_CONTACT_OPTIONS,
      getProjectStepStatusTypeTranslated,
      handleOpenActivityDialog,
      handleClickOpenCreateSubTaskDialog,
      getProjectStepPriorityTypeTranslated,
    ]
  );

  const handleCloseUpdateProjectStepDialog = () => setOpenUpdateProjectStepDialog(false);
  const handleClickOpenUpdateProjectStepDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenUpdateProjectStepDialog(true);
  };

  const handleCloseDeleteProjectStepDialog = () => setOpenDeleteProjectStepDialog(false);
  const handleClickOpenDeleteProjectStepDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenDeleteProjectStepDialog(true);
  };

  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) setUsers(response.data);
    } catch {}
  };

  const getActivityTypes = async () => {
    try {
      const response = await apiService.getActivityTypes();

      if (response.isSuccess) setActivityTypes(response.data);
    } catch {}
  };

  const getBooks = async () => {
    try {
      const response = await apiService.getBooks();

      if (response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setBooks(sortedData);
      }
    } catch {}
  };

  const getProjectSteps = useCallback(async () => {
    const postData: {
      statusType: number | null;
      projectId: number | null;
      bookId: number | null;
      contactId: number | null;
    } = {
      statusType,
      projectId: selectedProduct?.id ?? null,
      bookId: selectedBook?.id ?? null,
      contactId: selectedContact?.id ?? null,
    };

    try {
      setLoading(true);

      const response = await apiService.getProjectSteps(postData);

      if (response.isSuccess) {
        const bookMap = new Map(books.map((b) => [b.id, b.companyName]));

        const sortedData = [...response.data]
          .sort((a, b) => {
            const companyA = bookMap.get(a.bookId) || '';
            const companyB = bookMap.get(b.bookId) || '';

            const companyCompare = companyA.localeCompare(companyB);
            if (companyCompare !== 0) return companyCompare;

            return sortByDueDate(a, b);
          })
          .map((item) => ({
            ...item,
            subTasks: [...(item.subTasks || [])].sort(sortByDueDate),
          }));

        setProjectSteps(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [statusType, books, selectedBook, selectedProduct, selectedContact]);

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-project-task')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getUsers();
    getBooks();
    getActivityTypes();
  }, []);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    getProjectSteps();
  }, [getProjectSteps]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-project-task']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenCreateProjectStepDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="projectStepManaging.createProjectStep" defaultMessage="Proje Adımı Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['update-project-task']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenUpdateProjectStepDialog}
              fullWidth
              size="medium"
              variant="contained"
              color="info"
              endIcon={<EditIcon />}>
              <FormattedMessage id="projectStepManaging.updateProjectStep" defaultMessage="Proje Adımı Güncelle" />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-project-task']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenDeleteProjectStepDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="projectStepManaging.deleteProjectStep" defaultMessage="Proje Adımı Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>

      <ChipsArray type="projectTask" statusType={statusType} setStatusType={setStatusType} />

      <DataGridBox>
        <ZebraDataGrid<ProjectStepGridRow>
          treeData
          // defaultGroupingExpansionDepth={-1}
          pagination
          checkboxSelection
          rowSelectionPropagation={{
            parents: false,
            descendants: false,
          }}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel((prev) => {
              const prevStr = JSON.stringify(prev);
              const nextStr = JSON.stringify(newRowSelectionModel);

              return prevStr === nextStr ? prev : newRowSelectionModel;
            });
          }}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          initialState={{
            aggregation: {
              model: {
                estimatedTaskDay: 'sum',
                additionalEffortDay: 'sum',
              },
            },
          }}
          disableRowSelectionOnClick
          getRowId={(row) => row.rowId}
          getTreeDataPath={getTreeDataPath}
          groupingColDef={{
            headerName: '',
            width: 70,
            hideDescendantCount: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            resizable: false,
          }}
          getRowHeight={(params: GridRowHeightParams) => {
            if (isAutogeneratedRow(params.model)) {
              return 44;
            }

            return 'auto';
          }}
          getRowClassName={(params) => (params.row.isSubTask ? 'sub-task-row' : 'parent-task-row')}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .comments-cell': {
              alignItems: 'flex-start',
              py: 1,
            },
            '& .sub-task-row': {
              position: 'relative',
              bgcolor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.info.main, 0.06)
                  : alpha(theme.palette.info.main, 0.03),

              '&::before': {
                content: '""',
                position: 'absolute',
                left: 8,
                top: 6,
                bottom: 6,
                width: 3,
                borderRadius: 2,
                backgroundColor: theme.palette.info.main,
                opacity: 0.5,
              },
            },
          }}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={gridRows}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              exportPremiumExcel: true,
              excelStyleRules: [
                {
                  field: 'comments',
                  width: 50,
                  wrapText: true,
                  horizontal: 'left',
                  vertical: 'top',
                  getRowHeight: (value) => {
                    const lineCount = value ? value.split(/\r\n|\r|\n/).length : 1;
                    return 20 + (lineCount - 1) * 15;
                  },
                },
                {
                  field: 'status',
                  width: 18,
                  getCellStyle: (value) => {
                    if (value === 'İster') {
                      return { bg: 'E8F1FD', fg: '1E88E5', border: '90CAF9' };
                    }
                    if (value === 'Devam Ediyor') {
                      return { bg: 'F3E5F5', fg: '8E24AA', border: 'CE93D8' };
                    }
                    if (value === 'Tamamlandı') {
                      return { bg: 'E8F5E9', fg: '2E7D32', border: 'A5D6A7' };
                    }
                    return null;
                  },
                },
                {
                  field: 'priority',
                  width: 16,
                  getCellStyle: (value) => {
                    if (value === 'Düşük') {
                      return { bg: 'E8F5E9', fg: '2E7D32', border: 'A5D6A7' };
                    }
                    if (value === 'Orta') {
                      return { bg: 'FFF8E1', fg: 'F9A825', border: 'FFE082' };
                    }
                    if (value === 'Yüksek') {
                      return { bg: 'FFEBEE', fg: 'C62828', border: 'EF9A9A' };
                    }
                    return null;
                  },
                },
              ],
              showTemplates: true,
              apiRef,
              pageName,
              singleFilter: true,
              singleFilterComponent: (
                <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
                  <Box>
                    <FormattedMessage id="projectStepManaging.book" defaultMessage="Proje" />
                    &#58;&nbsp;
                  </Box>
                  <Autocomplete
                    size="small"
                    sx={{ width: '10vw' }}
                    options={books}
                    value={selectedBook}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Book | null) => setSelectedBook(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Divider orientation="vertical" sx={{ marginX: 1, border: 0 }} />
                  <Box>
                    <FormattedMessage id="project.table.product" defaultMessage="Ürün" />
                    &#58;&nbsp;
                  </Box>
                  <Autocomplete
                    size="small"
                    sx={{ width: '10vw' }}
                    options={products}
                    value={selectedProduct}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Project | null) => setSelectedProduct(newValue)}
                    getOptionLabel={(option: Project) => option.title ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Divider orientation="vertical" sx={{ marginX: 1, border: 0 }} />
                  <Box>
                    <FormattedMessage id="project.table.relatedContact" defaultMessage="İlgili Kişi" />
                    &#58;&nbsp;
                  </Box>
                  <Autocomplete
                    size="small"
                    sx={{ width: '10vw' }}
                    options={contactOptions}
                    value={selectedContact}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: User | null) => setSelectedContact(newValue)}
                    getOptionLabel={(option: User) => option.nameSurname ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              ),
            },
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
          apiRef={apiRef}
        />
      </DataGridBox>

      <CreateProjectStepDialog
        open={openCreateProjectStepDialog}
        handleClose={handleCloseCreateProjectStepDialog}
        books={books}
        products={products}
        users={users}
        priorityOptions={PRIORITY_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProjectSteps}
        mode={createMode}
        parentTask={parentTaskForCreate}
      />
      <UpdateProjectStepDialog
        open={openUpdateProjectStepDialog}
        handleClose={handleCloseUpdateProjectStepDialog}
        projectStepId={selectedRow?.id ?? null}
        books={books}
        products={products}
        users={users}
        priorityOptions={PRIORITY_OPTIONS}
        statusOptions={STATUS_OPTIONS}
        responsibleContactOptions={RESPONSIBLE_CONTACT_OPTIONS}
        refresh={getProjectSteps}
      />
      <DeleteProjectStepDialog
        open={openDeleteProjectStepDialog}
        projectStepId={selectedRow?.id ?? null}
        handleClose={handleCloseDeleteProjectStepDialog}
        refresh={getProjectSteps}
        setRowSelectionModel={setRowSelectionModel}
      />
      <AddCommentPopover<ProjectStepGridRow>
        open={commentPopover.open}
        anchorEl={commentPopover.anchorEl}
        row={commentPopover.row}
        loading={commentPopover.loading}
        onClose={handleCloseAddComment}
        onSubmit={handleSubmitAddComment}
      />
      <DeleteCommentDialog
        open={openDeleteCommentDialog}
        id={commentIdWillBeDeleted}
        projectStepId={rowIdForDeleteComment}
        handleClose={handleCloseDeleteCommentDialog}
        setProjectSteps={setProjectSteps}
      />
      <DetailDrawer
        open={openDetail}
        close={closeRowDetail}
        detailRow={detailRow}
        books={books}
        products={products}
        users={users}
        getProjectStepStatusTypeTranslated={getProjectStepStatusTypeTranslated}
        getProjectStepPriorityTypeTranslated={getProjectStepPriorityTypeTranslated}
        getProjectStepResponsibleContactTypeTranslated={getProjectStepResponsibleContactTypeTranslated}
      />
      <CreateActivityBoardItemDialog
        open={openActivityDialog}
        handleClose={handleCloseActivityDialog}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        initialData={activityInitialData}
        onSuccess={() => navigate('/activity-managing')}
      />
    </>
  );
};

export default ProjectStepManaging;
